import { LOGOS } from './Logo';
import { ModelName, MODEL_NAMES } from './modelNames';

export interface ModelOption {
  logoSrc: string;
  modelName: ModelName;
  description: string;
  provider: string;
  /** Only listed in chat when the org has enabled this family on LLM Keys. */
  orgOnly?: boolean;
}

// Default model to use when no model is selected
export const DEFAULT_MODEL = MODEL_NAMES.RECOMMENDED;

export const MODEL_OPTIONS: ModelOption[] = [
  {
    logoSrc: '/icons/stars.svg',
    modelName: MODEL_NAMES.RECOMMENDED,
    description: 'AI-selected best model for your task',
    provider: '', // Empty provider means it won't be filtered out by org settings
  },
  // {
  //   logoSrc: LOGOS.OPENAI,
  //   modelName: 'GPT-4',
  //   description: 'Most capable model, great for complex tasks',
  //   provider: 'OpenAI',
  // },
  {
    logoSrc: LOGOS.OPENAI,
    modelName: MODEL_NAMES.OPENAI,
    description: 'Fast and efficient model for quick responses',
    provider: 'OpenAI',
  },
  {
    logoSrc: LOGOS.ANTHROPIC,
    modelName: MODEL_NAMES.ANTHROPIC,
    description: 'Advanced reasoning and analysis',
    provider: 'Anthropic',
  },
  {
    logoSrc: LOGOS.GEMINI,
    modelName: MODEL_NAMES.GEMINI,
    description: 'Great for coding and technical tasks',
    provider: 'Gemini',
  },
  {
    logoSrc: LOGOS.COHERE,
    modelName: MODEL_NAMES.COHERE,
    description: 'High-performance model for enterprise tasks',
    provider: 'Cohere',
  },
  {
    logoSrc: LOGOS.XAI,
    modelName: MODEL_NAMES.XAI,
    description: 'Conversational AI with real-time data access',
    provider: 'XAI',
  },
  {
    logoSrc: LOGOS.MISTRAL,
    modelName: MODEL_NAMES.MISTRAL,
    description: 'Self-managed Mistral chat models',
    provider: 'Mistral',
    orgOnly: true,
  },
  {
    logoSrc: LOGOS.DEEPSEEK,
    modelName: MODEL_NAMES.DEEPSEEK,
    description: 'Self-managed DeepSeek chat models',
    provider: 'DeepSeek',
    orgOnly: true,
  },
  {
    logoSrc: LOGOS.NVIDIA,
    modelName: MODEL_NAMES.NVIDIA,
    description: 'Self-managed NVIDIA NIM chat models',
    provider: 'NVIDIA',
    orgOnly: true,
  },
  {
    logoSrc: LOGOS.ALIBABA,
    modelName: MODEL_NAMES.ALIBABA,
    description: 'Self-managed Alibaba Qwen chat models',
    provider: 'Alibaba',
    orgOnly: true,
  },
  {
    logoSrc: LOGOS.AI21,
    modelName: MODEL_NAMES.AI21,
    description: 'Self-managed AI21 Jamba chat models',
    provider: 'AI21',
    orgOnly: true,
  },
  {
    logoSrc: LOGOS.GROQ,
    modelName: MODEL_NAMES.GROQ,
    description: 'Self-managed Groq inference',
    provider: 'Groq',
    orgOnly: true,
  },
  {
    logoSrc: LOGOS.TOGETHER,
    modelName: MODEL_NAMES.TOGETHER,
    description: 'Self-managed Together AI inference',
    provider: 'Together',
    orgOnly: true,
  },
  {
    logoSrc: LOGOS.PERPLEXITY,
    modelName: MODEL_NAMES.PERPLEXITY,
    description: 'Self-managed Perplexity Sonar models',
    provider: 'Perplexity',
    orgOnly: true,
  },
  {
    logoSrc: LOGOS.HUGGINGFACE,
    modelName: MODEL_NAMES.HUGGINGFACE,
    description: 'Self-managed Hugging Face Inference Router',
    provider: 'HuggingFace',
    orgOnly: true,
  },
];

/**
 * Get the appropriate logo for a given model name by matching keywords
 * @param modelName - The name of the model to get logo for
 * @returns The logo URL or empty string if no match found
 */
export const getModelLogo = (modelName: string): string => {
  if (!modelName) return '';

  const lowerModelName = modelName.toLowerCase();

  // Check for Recommended Model first
  if (lowerModelName.includes('recommended')) {
    return '/icons/stars.svg';
  }

  // Define keyword-to-logo mappings based on MODEL_OPTIONS
  const keywordMappings = [
    { keywords: ['gpt'], logo: LOGOS.OPENAI },
    { keywords: ['claude'], logo: LOGOS.ANTHROPIC },
    { keywords: ['gemini'], logo: LOGOS.GEMINI },
    { keywords: ['llama'], logo: LOGOS.META },
    { keywords: ['cohere'], logo: LOGOS.COHERE },
    { keywords: ['grok'], logo: LOGOS.XAI },
    { keywords: ['mistral'], logo: LOGOS.MISTRAL },
    { keywords: ['deepseek'], logo: LOGOS.DEEPSEEK },
    { keywords: ['nvidia'], logo: LOGOS.NVIDIA },
    { keywords: ['alibaba', 'qwen'], logo: LOGOS.ALIBABA },
    { keywords: ['ai21', 'jamba'], logo: LOGOS.AI21 },
    { keywords: ['groq'], logo: LOGOS.GROQ },
    { keywords: ['together'], logo: LOGOS.TOGETHER },
    { keywords: ['perplexity', 'sonar'], logo: LOGOS.PERPLEXITY },
    { keywords: ['hugging'], logo: LOGOS.HUGGINGFACE },
  ];

  // Find matching logo by keyword
  for (const mapping of keywordMappings) {
    if (mapping.keywords.some((keyword) => lowerModelName.includes(keyword))) {
      return mapping.logo;
    }
  }

  return '';
};

/**
 * Get the provider for a given model name
 * @param modelName - The name of the model to get provider for
 * @returns The provider name or undefined if model not found
 */
export const getProviderForModel = (modelName: string): string | undefined => {
  if (!modelName) return undefined;

  const model = MODEL_OPTIONS.find((opt) => opt.modelName === modelName);
  return model?.provider;
};

/**
 * Filter models based on enabled providers
 * @param models - Array of model options to filter
 * @param enabledProviders - Array of enabled provider names
 * @returns Filtered array of models that match the enabled providers
 */
export const filterModelsByProviders = <T extends { provider?: string; orgOnly?: boolean }>(
  models: T[],
  enabledProviders: string[] | undefined,
): T[] => {
  // If undefined or null, return all models (no restrictions) except org-only BYOK families.
  if (enabledProviders === undefined || enabledProviders === null) {
    return models.filter((model) => !model.orgOnly);
  }

  // If empty array, return only models with no provider (e.g., "Recommended Model")
  // This means all provider-based models are disabled
  if (enabledProviders.length === 0) {
    return models.filter((model) => !model.provider);
  }

  return models.filter((model) => {
    const provider = model.provider;
    if (model.orgOnly) {
      return Boolean(provider && enabledProviders.includes(provider));
    }
    // If model has no provider, allow it (e.g., "Recommended Model")
    if (!provider) return true;
    // Otherwise, check if provider is in the enabled list
    return enabledProviders.includes(provider);
  });
};

/**
 * Check if a specific model is allowed based on enabled providers
 * @param modelName - The name of the model to check
 * @param enabledProviders - Array of enabled provider names
 * @returns True if the model is allowed, false otherwise
 */
export const isModelAllowed = (
  modelName: ModelName,
  enabledProviders: string[] | undefined,
): boolean => {
  const option = MODEL_OPTIONS.find((opt) => opt.modelName === modelName);
  const provider = option?.provider;

  // If no provider restrictions, all built-in models are allowed; BYOK stays hidden.
  if (!enabledProviders || enabledProviders.length === 0) {
    return !option?.orgOnly;
  }

  if (option?.orgOnly) {
    return Boolean(provider && enabledProviders.includes(provider));
  }

  // If model has no provider, allow it
  if (!provider) return true;

  // Check if provider is in the enabled list
  return enabledProviders.includes(provider);
};

/**
 * Get the actual model to use when "Recommended Model" is selected.
 * Dynamically picks the best available model based on organization restrictions and priority order.
 * @param enabledProviders - Optional array of enabled provider names for the organization
 * @param customOrder - Optional prioritized list of model names to choose from
 * @returns The model name to use for the recommended model
 * @throws Error if no models are available (when enabledProviders is an empty array)
 */
export const getRecommendedModel = (
  enabledProviders?: string[],
  customOrder?: ModelName[],
): ModelName => {
  // Use provided order or default to the order defined in MODEL_OPTIONS
  // IMPORTANT: We must filter out "Recommended Model" from the order to avoid recursion
  const baseOrder = customOrder || MODEL_OPTIONS.map((opt) => opt.modelName);

  const recommendationOrder = baseOrder.filter((name) => name !== MODEL_NAMES.RECOMMENDED);

  // If enabledProviders is explicitly an empty array, no models are available
  if (enabledProviders?.length === 0) {
    throw new Error(
      'No AI models are enabled for your organization. Please contact your administrator.',
    );
  }

  // If no restrictions (undefined), use our top preferred choice
  if (!enabledProviders) {
    return recommendationOrder[0] || MODEL_NAMES.OPENAI;
  }

  // Find the first model in our recommendation order that matches an enabled provider
  for (const modelName of recommendationOrder) {
    if (isModelAllowed(modelName, enabledProviders)) {
      return modelName;
    }
  }

  // Fallback: If none of our preferred models are allowed, find ANY allowed real model
  const anyAllowed = MODEL_OPTIONS.find(
    (opt) =>
      opt.modelName !== MODEL_NAMES.RECOMMENDED && isModelAllowed(opt.modelName, enabledProviders),
  );

  if (!anyAllowed) {
    throw new Error(
      'No AI models are enabled for your organization. Please contact your administrator.',
    );
  }

  return anyAllowed.modelName;
};
